// Copyright 2016 Sorama B.V.

import { Container, MD5FileHash, ReorderProperties } from "datatypes/SoramaLocalApi";
import { GenericMeasurement } from "datatypes/SoramaWebApi";

export abstract class StorageTypes {
    public static media = "Media";
    public static measurement = "Measurement";
    public static recording = "Recording";
    public static recordingGroup = "RecordingGroup";
    public static sorMeasurement = "SorMeasurement";
}

export abstract class ITypedStorageContainerSet {
    /**
     * Gets the currently available containers (to update containers use Refresh())
     */
    public containers: Container[];

    public isRefreshing: boolean;

    public abstract refresh(): Promise<Container[]>;

    public abstract dispose(): void;
}

export abstract class IStorageService {
    //#region Create, Read and Delete Calls

    /**
     * Create a new empty container
     */
    public abstract create(type: string): Promise<string>;

    /**
     * Refreshes the currently available containers
     */
    public abstract enumerate(type: string): Promise<Container[]>;

    /**
     * Returns a storage container with the given type
     * @param type
     */
    public abstract getTypedStorageContainerSet(type: string): Promise<ITypedStorageContainerSet>;

    /**
     * Returns the names of all files in the given container
     */
    public abstract getFiles(containerId: string): Promise<string[]>;

    /**
     * Delete a given container
     * @param containerId Id of the container to delete
     */
    public abstract delete(containerId: string): Promise<void>;

    /**
     * Load a container with metadata
     * @param containerId Id of the container
     */
    public abstract load(containerId: string): Promise<Container>;
    public abstract getSorMetadata(sorId: string): Promise<GenericMeasurement>;

    //#endregion

    //#region Pseudo-Properties

    /**
     * Get the physical path on the disk of sor file with specified id.
     * @param sorId  Id of the sor measurement to get the path for
     */
    public abstract getSorPath(sorId: string): Promise<string>;

    /**
     * Get the physical path on the disk of the container with the specified id.
     * @param containerId Id of the container to get the path for
     */
    public abstract getPath(containerId: string): Promise<string>;

    /**
     * Get metadata object for the given container
     * @param containerId Id of the container to get the metadata from
     */
    public abstract getMetadata<T>(containerId: string): Promise<T>;

    /**
     * Get metadata value for a given key and container
     * @param containerId Id of the container to get the metadata from
     * @param key Key of the metadata value to get
     */
    public abstract getMetadataValue<T>(containerId: string, key: string): Promise<T>;

    /**
     * Gets the MD5 hash and file size for the requested file in the requested container
     * @param containerId Id of the container to find the file in
     * @param file Name of the file
     */
    public abstract getMD5Hash(containerId: string, file: string): Promise<MD5FileHash>;

    /**
     * Gets the file size for the requested file in the requested container
     * @param containerId Id of the container to find the file in
     * @param file Name of the file
     */
    public abstract getFileSize(containerId: string, file: string): Promise<number>;

    /**
     * Gets the file size map for the requested containerId in the requested recording group
     * @param containerId Id of the recording group container
     */
    public abstract getRecordingSizeMap(containerId: string): Promise<Map<string, number>>;

    /**
     * Gets the sum of files size for the requested recordingGroup container
     * @param containerId Id of the recording group container
     */
    public abstract getRecordingGroupSize(containerId: string): Promise<number>;

    /**
     * Gets the map of containerId to file size map for the in the requested recording groups
     * @param containerId Ids of the recording group containers
     */
    public abstract getRecordingSizeMaps(containerId: string[]): Promise<Map<string, Map<string, number>>>;

    /**
     * Gets the sum of files size for the all requested recordingGroup containers
     * @param containerId Ids of the recording group containers
     */
    public abstract getRecordingGroupSizes(containerId: string[]): Promise<Map<string, number>>;

    /**
     * Get binary file data from a file in a container
     * @param containerId Id of the container to retrieve file from
     * @param file File name of the file to retreive
     * @param offset Offset of the file data
     * @param size Size of the file data
     */
    public abstract getRaw(containerId: string, file: string, offset: number, size: number): Promise<Blob>;

    /**
     * Get binary file data from a file in the measurement folder
     * @param sorId Id of the sor measurement to retreive file from
     */
    public abstract getRawSor(sorId: string): Promise<Blob>;

    /**
     * Export recording groups and their recordings to a zip file and download
     * @param groupIds Ids of the groups to export.
     * @param zipFileName Filename of the zip. (without extension)
     */
    public abstract exportDataZip(groupIds: string[], zipFileName?: string): Promise<void>;

    /**
     * Gets recording groups and their recordings to a zip file and return as blob.
     * @param groupIds Ids of the groups to export.
     * @param zipFileName Filename of the zip. (without extension)
     */
    public abstract getDataZip(groupIds: string[], zipFileName?: string): Promise<Blob>;

    /**
     * Gets the sor file and return as blob.
     * @param sorId sorId of the file to get
     */
    public abstract getSor(sorId: string): Promise<Blob>;

    /**
     * Gets the sor files in a zip file and return as blob.
     * @param sorIds sorIds of the files to get
     */
    public abstract getSorZip(sorIds: string[], zipFileName?: string): Promise<Blob>;

    /**
     * Add new metadata key value pair to the given container
     * @param containerId Id of the container to add the metadata to
     * @param key Key of the metadata value to set
     * @param value Value of the metadata
     */
    public abstract updateMetadata(containerId: string, key: string, value: unknown): Promise<void>;

    /**
     * Add a set of metadata key values to the given container
     * @param containerId Id of the container to add the metadata to
     * @param kvps Set of key value pairs with the metadata to add/update
     */
    public abstract updateMetadataSet(containerId: string, kvps: { [key: string]: unknown }): Promise<void>;

    /**
     * Add new metadata key value pair to the given container and a given file within that container
     * @param containerId Id of the container to add the metadata to
     * @param file Name of the file within the container to add the metadata to
     * @param key Key of the metadata value to set
     * @param value Value of the metadata
     */
    public abstract updateFileMetadata(containerId: string, file: string, key: string, value: unknown): Promise<void>;

    /**
     * Copy data from container storage to measurement storage
     * @param containerId Id of the container storage to copy data form
     * @param measurementId Id of the measurement storage to copy to
     * @param containerFile File to copy from the container storage
     * @param measurementFile File to copy to in the measurement storage
     */
    public abstract toMeasurementStorage(
        containerId: string,
        measurementId: string,
        containerFile: string,
        measurementFile: string
    ): Promise<void>;

    /**
     * Copy date from container(s) to the measurement storage, performing a binary merge of two or more files as specified in the properties.
     * @param measurementId Id of the measurement storage to copy to
     * @param reorderProperties Properties specifying the input files and way to merge them
     */
    public abstract toMeasurementStorageReordered(
        measurementId: string,
        reorderProperties: ReorderProperties
    ): Promise<void>;

    /**
     * Create a new text file in the specified measurements storage
     * @param measurementId Id of the measurement storage
     * @param fileName Name of the file to be created, file must not exist
     * @param fileContent Contents of the file
     */
    public abstract createMeasurementStorageTextFile(
        measurementId: string,
        fileName: string,
        fileContent: string
    ): Promise<void>;

    /**
     * Checks if a measurements data is available
     * @param measurement
     */
    public abstract areMeasurementsAvailable(measurements: GenericMeasurement[]): Promise<{ [key: string]: boolean }>;

    public abstract openInExplorer(path: string): Promise<void>;
    //#endregion
}
